import React, { useCallback, useMemo, useState } from "react";

import {
  FlamebearerCall,
  FlamebearerNode
} from "@platformsh/observability-graph/ContinuousProfiling";

import { debounce } from "ds/utils/debounce";

import DisplayGraphIcon from "./displayIcon/DisplayGraphIcon";
import DisplayResetIcon from "./displayIcon/DisplayResetIcon";
import DisplaySplitIcon from "./displayIcon/DisplaySplitIcon";
import DisplayTableIcon from "./displayIcon/DisplayTableIcon";
import * as S from "./Flamebearer.styles";
import FlamebearerGraph from "./graph/FlamebearerGraph";
import SearchInput from "./SearchInput";
import FlamebearerTable from "./table/FlamebearerTable";

type DisplayMode = "table" | "graph" | "split";

const Flamebearer = () => {
  const [filter, setFilter] = React.useState("");
  const [displayMode, setDisplayMode] = React.useState<DisplayMode>("split");

  const [highlightedCall, setHighlightedCall] =
    useState<FlamebearerCall | null>(null);
  const [selectedNode, setSelectedNode] = useState<FlamebearerNode | null>(
    null
  );
  const handleNodeSelected = useCallback((node: FlamebearerNode | null) => {
    if (!node || node.level === 0) {
      setSelectedNode(null);
    } else {
      setSelectedNode(node);
    }
  }, []);

  const handleCallHighlighted = useMemo(() => {
    return debounce(setHighlightedCall);
  }, []);
  const handleTableClick = useCallback(() => {
    setDisplayMode("table");
  }, []);
  const handleGraphClick = useCallback(() => {
    setDisplayMode("graph");
  }, []);
  const handleSplitClick = useCallback(() => {
    setDisplayMode("split");
  }, []);
  const handleResetClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  return (
    <S.Layout>
      <S.Controls>
        <S.Filter>
          <SearchInput onChange={setFilter} />
        </S.Filter>
        <S.Buttons>
          <S.Button
            active={displayMode === "split"}
            content="icon"
            onClick={handleSplitClick}
          >
            <DisplaySplitIcon />
          </S.Button>
          <S.Button
            active={displayMode === "table"}
            content="icon"
            onClick={handleTableClick}
          >
            <DisplayTableIcon />
          </S.Button>
          <S.Button
            active={displayMode === "graph"}
            content="icon"
            onClick={handleGraphClick}
          >
            <DisplayGraphIcon />
          </S.Button>
          <S.Button
            content="icon"
            onClick={handleResetClick}
            disabled={!selectedNode}
          >
            <DisplayResetIcon />
          </S.Button>
        </S.Buttons>
      </S.Controls>
      <hr />
      <S.Row>
        {displayMode !== "graph" && (
          <S.Cell>
            <FlamebearerTable
              filter={filter}
              highlightedCall={highlightedCall}
              onCallHighlighted={handleCallHighlighted}
            />
          </S.Cell>
        )}
        {displayMode !== "table" && (
          <S.Cell>
            <FlamebearerGraph
              highlightedCall={highlightedCall}
              onCallHighlighted={handleCallHighlighted}
              selectedNode={selectedNode}
              onNodeSelected={handleNodeSelected}
            />
          </S.Cell>
        )}
      </S.Row>
    </S.Layout>
  );
};

export default Flamebearer;
